import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Upload,
  Save,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Palette,
  Image,
  X,
  CheckCircle,
  AlertCircle,
  Eye,
  FileText,
  Loader2,
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface ProjectBranding {
  id: number;
  name: string;
  client_name: string | null;
  created_at: string;
  branding: {
    id: number;
    company_name: string;
    primary_color: string;
    secondary_color: string | null;
    accent_color: string | null;
    logo_filename: string | null;
    logo_s3_key: string | null;
    updated_at: string;
  } | null;
}

interface BrandingForm {
  companyName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  // S3 state
  pendingLogoFile: File | null;      // file chosen but not yet uploaded
  uploadedS3Key: string | null;      // returned after upload
  uploadedFilename: string | null;   // display name after upload
  // existing logo (from DB)
  existingFilename: string | null;
  existingS3Key: string | null;
  // preview URL (presigned or local object URL)
  previewUrl: string | null;
}

const DEFAULT_FORM: BrandingForm = {
  companyName: '',
  primaryColor: '#1D58A4',
  secondaryColor: '#164A8C',
  accentColor: '#F59E0B',
  pendingLogoFile: null,
  uploadedS3Key: null,
  uploadedFilename: null,
  existingFilename: null,
  existingS3Key: null,
  previewUrl: null,
};

// Pre-populated brand palettes — sourced from each client's public brand guidelines.
// Used as smart defaults when a project for one of these clients has no branding configured yet.
const BRAND_DEFAULTS: { match: string[]; primary: string; secondary: string; accent: string }[] = [
  { match: ['american express', 'amex'], primary: '#006FCF', secondary: '#00175A', accent: '#FFFFFF' },
  { match: ['pacific life'],              primary: '#003D7A', secondary: '#0064A4', accent: '#FFC72C' },
  { match: ['smbc', 'sumitomo mitsui'],   primary: '#0F8B7E', secondary: '#005C4D', accent: '#A6CE39' },
  { match: ['stemmler', 'stemler', 'tsg'],primary: '#1D58A4', secondary: '#164A8C', accent: '#F59E0B' },
];

const getBrandDefaults = (clientName: string | null, projectName: string) => {
  const haystack = `${clientName || ''} ${projectName}`.toLowerCase();
  const hit = BRAND_DEFAULTS.find(b => b.match.some(m => haystack.includes(m)));
  if (!hit) return null;
  return { primaryColor: hit.primary, secondaryColor: hit.secondary, accentColor: hit.accent };
};

const ClientBrandingAdmin: React.FC = () => {
  const [projects, setProjects] = useState<ProjectBranding[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState<number | null>(null);
  const [forms, setForms] = useState<Record<number, BrandingForm>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [uploading, setUploading] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const formsRef = useRef<Record<number, BrandingForm>>({});

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/branding/projects');
      const data: ProjectBranding[] = res.data.data || [];
      setProjects(data);

      setForms(prev => {
        const next: Record<number, BrandingForm> = {};
        data.forEach(p => {
          const brandPreset = getBrandDefaults(p.client_name, p.name);
          next[p.id] = p.branding ? {
            companyName: p.branding.company_name,
            primaryColor: p.branding.primary_color || brandPreset?.primaryColor || '#1D58A4',
            secondaryColor: p.branding.secondary_color || brandPreset?.secondaryColor || '#164A8C',
            accentColor: p.branding.accent_color || brandPreset?.accentColor || '#F59E0B',
            pendingLogoFile: null,
            uploadedS3Key: null,
            uploadedFilename: null,
            existingFilename: p.branding.logo_filename,
            existingS3Key: p.branding.logo_s3_key,
            previewUrl: prev[p.id]?.previewUrl ?? null,
          } : {
            ...DEFAULT_FORM,
            ...(brandPreset || {}),
            companyName: p.client_name || p.name,
          };
        });
        formsRef.current = next;
        return next;
      });
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  // Fetch logo whenever expanded project has an S3 key but no preview yet
  // Watches both expandedProject AND forms so it fires after fetchProjects loads data
  const fetchingLogoRef = useRef<number | null>(null);
  useEffect(() => {
    if (expandedProject === null) return;
    const form = forms[expandedProject];
    if (form?.existingS3Key && !form.previewUrl && !form.pendingLogoFile && fetchingLogoRef.current !== expandedProject) {
      fetchingLogoRef.current = expandedProject;
      api.get(`/api/projects/${expandedProject}/branding/logo`, { responseType: 'arraybuffer' })
        .then(res => {
          const bytes = new Uint8Array(res.data);
          let binary = '';
          bytes.forEach(b => { binary += String.fromCharCode(b); });
          const b64 = btoa(binary);
          const mime = res.headers['content-type'] || 'image/svg+xml';
          const dataUrl = `data:${mime};base64,${b64}`;
          setForms(prev => {
            const updated = { ...prev, [expandedProject]: { ...prev[expandedProject], previewUrl: dataUrl } };
            formsRef.current = updated;
            return updated;
          });
        })
        .catch(() => { fetchingLogoRef.current = null; });
    }
  }, [expandedProject, forms]);

  const handleExpand = (projectId: number) => {
    setExpandedProject(prev => {
      if (prev === projectId) { fetchingLogoRef.current = null; return null; }
      return projectId;
    });
  };

  // Step 1: user picks file → read as base64 data URL for CSP-safe preview
  const handleFileSelect = (projectId: number, file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setForms(prev => {
        const updated = { ...prev, [projectId]: { ...prev[projectId], pendingLogoFile: file, previewUrl: dataUrl, uploadedS3Key: null, uploadedFilename: null } };
        formsRef.current = updated;
        return updated;
      });
    };
    reader.readAsDataURL(file);
  };

  // Step 2: upload file to S3 via backend
  const uploadLogoToS3 = async (projectId: number): Promise<{ s3Key: string; filename: string } | null> => {
    const form = forms[projectId];
    if (!form.pendingLogoFile) return null;

    setUploading(projectId);
    try {
      const formData = new FormData();
      formData.append('logo', form.pendingLogoFile);
      const res = await api.post(`/api/projects/${projectId}/branding/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { s3Key, filename } = res.data;
      setForms(prev => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          uploadedS3Key: s3Key,
          uploadedFilename: filename,
          pendingLogoFile: null,
        },
      }));
      return { s3Key, filename };
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Logo upload failed');
      return null;
    } finally {
      setUploading(null);
    }
  };

  // Step 3: save branding metadata (colors, name, s3Key)
  const handleSave = async (projectId: number) => {
    const form = forms[projectId];
    if (!form) return;

    if (!form.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    setSaving(projectId);
    try {
      // Upload logo first if a new file was selected
      let logoS3Key = form.uploadedS3Key || form.existingS3Key;
      let logoFilename = form.uploadedFilename || form.existingFilename;

      if (form.pendingLogoFile) {
        const result = await uploadLogoToS3(projectId);
        if (!result) { setSaving(null); return; }
        logoS3Key = result.s3Key;
        logoFilename = result.filename;
      }

      await api.post(`/api/projects/${projectId}/branding`, {
        companyName: form.companyName,
        primaryColor: form.primaryColor,
        secondaryColor: form.secondaryColor || null,
        accentColor: form.accentColor || null,
        ...(logoS3Key !== undefined && { logoS3Key }),
        ...(logoFilename !== undefined && { logoFilename }),
        colorPalette: buildPalette(form),
      });

      toast.success('Branding saved');
      await fetchProjects();
      // Re-trigger logo fetch for expanded project by cycling expandedProject
      if (logoS3Key) {
        fetchingLogoRef.current = null;
        setExpandedProject(null);
        setTimeout(() => setExpandedProject(projectId), 50);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save branding');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (projectId: number) => {
    if (!window.confirm('Remove branding for this project?')) return;
    setDeleting(projectId);
    try {
      await api.delete(`/api/projects/${projectId}/branding`);
      toast.success('Branding removed');
      fetchProjects();
    } catch {
      toast.error('Failed to remove branding');
    } finally {
      setDeleting(null);
    }
  };

  const removePendingLogo = (projectId: number) => {
    setForms(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        pendingLogoFile: null,
        uploadedS3Key: null,
        uploadedFilename: null,
        previewUrl: prev[projectId].existingS3Key ? prev[projectId].previewUrl : null,
      },
    }));
  };

  const updateForm = (projectId: number, field: keyof BrandingForm, value: any) => {
    setForms(prev => {
      const updated = { ...prev, [projectId]: { ...prev[projectId], [field]: value } };
      formsRef.current = updated;
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">Loading projects...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Palette className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Document Style Guides</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Configure logo and colors per brand — applied to SOP and document headers/footers
              </p>
            </div>
          </div>
          <button
            onClick={fetchProjects}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <strong>How it works:</strong> Set a company name, logo (SVG/PNG/JPG), and brand colors per brand.
          These are embedded into the cover page and footer of every exported SOP document.
        </div>
      </div>

      {/* Project list */}
      <div className="space-y-3">
        {projects.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
            No projects found.
          </div>
        )}

        {projects.map(project => {
          const form = forms[project.id] || { ...DEFAULT_FORM, companyName: project.client_name || project.name };
          const isExpanded = expandedProject === project.id;
          const hasBranding = !!project.branding;
          const isSaving = saving === project.id;
          const isUploading = uploading === project.id;
          const hasNewFile = !!form.pendingLogoFile;
          const hasLogo = hasNewFile || !!form.existingFilename;

          return (
            <div key={project.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Row header */}
              <button
                onClick={() => handleExpand(project.id)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className="w-8 h-8 rounded-lg border border-gray-200 flex-shrink-0"
                    style={{ backgroundColor: project.branding?.primary_color || '#E5E7EB' }}
                  />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{project.name}</span>
                      {project.client_name && project.client_name !== project.name && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                          {project.client_name}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5">
                      {hasBranding ? (
                        <span className="flex items-center text-xs text-green-600">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {project.branding!.company_name}
                          {project.branding!.logo_filename && (
                            <span className="ml-2 text-gray-400">· {project.branding!.logo_filename}</span>
                          )}
                        </span>
                      ) : (
                        <span className="flex items-center text-xs text-gray-400">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          No branding configured
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {hasBranding && (
                    <ColorDots
                      primary={project.branding!.primary_color}
                      secondary={project.branding!.secondary_color}
                      accent={project.branding!.accent_color}
                    />
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {/* Expanded form */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-6 py-5 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: form fields */}
                    <div className="space-y-4">
                      {/* Company name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <input
                            type="text"
                            value={form.companyName}
                            onChange={e => updateForm(project.id, 'companyName', e.target.value)}
                            placeholder="e.g. Pacific Life Insurance"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Colors */}
                      <div className="grid grid-cols-3 gap-3">
                        <ColorField label="Primary" value={form.primaryColor} onChange={v => updateForm(project.id, 'primaryColor', v)} />
                        <ColorField label="Secondary" value={form.secondaryColor} onChange={v => updateForm(project.id, 'secondaryColor', v)} />
                        <ColorField label="Accent" value={form.accentColor} onChange={v => updateForm(project.id, 'accentColor', v)} />
                      </div>

                      {/* Logo upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Logo <span className="text-gray-400 font-normal">(SVG, PNG, JPG — max 5MB)</span>
                        </label>
                        <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                          <button
                            onClick={() => fileInputRefs.current[project.id]?.click()}
                            disabled={isUploading}
                            className="flex items-center space-x-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-gray-600 disabled:opacity-50"
                          >
                            <Upload className="w-4 h-4" />
                            <span>{hasNewFile ? 'Change file' : 'Choose file'}</span>
                          </button>
                          <input
                            ref={el => { fileInputRefs.current[project.id] = el; }}
                            type="file"
                            accept="image/svg+xml,image/png,image/jpeg"
                            className="hidden"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) handleFileSelect(project.id, file);
                              e.target.value = '';
                            }}
                          />

                          {/* Show pending or existing logo info */}
                          {hasNewFile && (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-700 px-2 py-1 rounded">
                                New: {form.pendingLogoFile!.name}
                              </span>
                              <button onClick={() => removePendingLogo(project.id)} className="text-gray-400 hover:text-red-500">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          {!hasNewFile && form.existingFilename && (
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <Image className="w-4 h-4 text-green-500" />
                              <span>{form.existingFilename}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: live preview */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Eye className="w-4 h-4 inline mr-1" />
                        Document Header Preview
                      </label>
                      <DocumentPreview
                        companyName={form.companyName || project.client_name || project.name}
                        primaryColor={form.primaryColor}
                        secondaryColor={form.secondaryColor}
                        previewUrl={form.previewUrl}
                        logoFilename={form.pendingLogoFile?.name || form.existingFilename}
                        logoMissing={!!(form.existingFilename && !form.existingS3Key && !form.pendingLogoFile)}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-200">
                    <div>
                      {hasBranding && project.branding?.updated_at && (
                        <span className="text-xs text-gray-400">
                          Last updated: {new Date(project.branding.updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      {hasBranding && (
                        <button
                          onClick={() => handleDelete(project.id)}
                          disabled={deleting === project.id}
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>{deleting === project.id ? 'Removing...' : 'Remove Branding'}</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleSave(project.id)}
                        disabled={isSaving || isUploading}
                        className="flex items-center space-x-2 px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {(isSaving || isUploading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        <span>
                          {isUploading ? 'Uploading logo...' : isSaving ? 'Saving...' : hasBranding ? 'Update Branding' : 'Save Branding'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const ColorDots: React.FC<{ primary: string; secondary: string | null; accent: string | null }> = ({ primary, secondary, accent }) => (
  <div className="flex items-center space-x-1 mr-2">
    <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: primary }} title={primary} />
    {secondary && <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: secondary }} title={secondary} />}
    {accent && <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: accent }} title={accent} />}
  </div>
);

const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-6 h-6 rounded border-0 cursor-pointer p-0" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} maxLength={7} className="flex-1 text-xs font-mono border-0 focus:outline-none bg-transparent w-16" />
    </div>
  </div>
);

const DocumentPreview: React.FC<{
  companyName: string;
  primaryColor: string;
  secondaryColor: string;
  previewUrl: string | null;
  logoFilename: string | null | undefined;
  logoMissing?: boolean;
}> = ({ companyName, primaryColor, secondaryColor, previewUrl, logoFilename, logoMissing }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
    <div className="p-4 text-center" style={{ borderBottom: `3px solid ${primaryColor}` }}>
      {previewUrl ? (
        <img src={previewUrl} alt="Logo" className="h-12 mx-auto mb-2 object-contain" />
      ) : logoMissing ? (
        <div className="h-12 mx-auto mb-2 flex items-center justify-center">
          <span className="text-xs bg-yellow-50 border border-yellow-300 rounded px-3 py-1.5 text-yellow-700 flex items-center space-x-1">
            <AlertCircle className="w-3 h-3" />
            <span>Re-upload logo to restore</span>
          </span>
        </div>
      ) : logoFilename ? (
        <div className="h-12 mx-auto mb-2 flex items-center justify-center">
          <span className="text-xs bg-gray-100 border border-gray-200 rounded px-3 py-1.5 text-gray-500 flex items-center space-x-1">
            <Image className="w-3 h-3" />
            <span>{logoFilename}</span>
          </span>
        </div>
      ) : (
        <div className="w-16 h-10 mx-auto mb-2 rounded flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: primaryColor }}>
          LOGO
        </div>
      )}
      <div className="text-sm font-bold text-gray-800">{companyName || 'Company Name'}</div>
      <div className="text-xs text-gray-500 mt-0.5">Division / Project Name</div>
    </div>
    <div className="px-4 py-3">
      <div className="text-sm font-bold text-center" style={{ color: primaryColor }}>Standard Operating Procedure</div>
      <div className="grid grid-cols-2 gap-x-4 mt-2 text-xs text-gray-500">
        <div>Process Owner: <span className="font-medium text-gray-700">—</span></div>
        <div>Version: <span className="font-medium text-gray-700">1.0</span></div>
        <div>Effective Date: <span className="font-medium text-gray-700">{new Date().toLocaleDateString()}</span></div>
        <div>Next Review: <span className="font-medium text-gray-700">—</span></div>
      </div>
    </div>
    <div className="px-4 py-2 flex items-center justify-between text-xs" style={{ backgroundColor: secondaryColor + '15', borderTop: `1px solid ${secondaryColor}30` }}>
      <span className="text-gray-500">Standard Operating Procedure – v1.0</span>
      <span className="text-gray-500">Page 1 of N</span>
    </div>
  </div>
);

// ── Color palette builder ──────────────────────────────────────────────────────

function buildPalette(form: BrandingForm) {
  const lighten = (hex: string, amt: number) => {
    try {
      const n = parseInt(hex.replace('#', ''), 16);
      const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      const to = (v: number) => Math.min(255, Math.round(v + (255 - v) * amt)).toString(16).padStart(2, '0');
      return `#${to(r)}${to(g)}${to(b)}`;
    } catch { return hex; }
  };
  const darken = (hex: string, amt: number) => {
    try {
      const n = parseInt(hex.replace('#', ''), 16);
      const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      const to = (v: number) => Math.max(0, Math.round(v * (1 - amt))).toString(16).padStart(2, '0');
      return `#${to(r)}${to(g)}${to(b)}`;
    } catch { return hex; }
  };
  const sec = form.secondaryColor || form.primaryColor;
  const acc = form.accentColor || '#F59E0B';
  return {
    primary: form.primaryColor, primaryLight: lighten(form.primaryColor, 0.3), primaryDark: darken(form.primaryColor, 0.2),
    secondary: sec, secondaryLight: lighten(sec, 0.3), secondaryDark: darken(sec, 0.2),
    accent: acc, accentLight: lighten(acc, 0.3), accentDark: darken(acc, 0.2),
    neutral: '#6B7280', neutralLight: '#9CA3AF', neutralDark: '#4B5563',
    success: '#10B981', warning: '#F59E0B', error: '#EF4444',
    textPrimary: '#1F2937', textSecondary: '#6B7280',
    background: '#FFFFFF', surface: '#F9FAFB',
  };
}

export default ClientBrandingAdmin;
